import React, { useEffect } from 'react'
import FormSection from './FormSection'
import { LuStethoscope, LuDollarSign, LuCalendar, LuShield, LuCircleAlert, LuActivity, LuSettings, LuCreditCard, LuPlus, LuCircleUser } from "react-icons/lu";
import moment from "moment-mini";

import AsyncSelect from 'Common/AsyncSelect/AsyncSelect';
import { GRACE_PERIOD_OPTIONS, PAYMENT_FREQUENCIES, PAYMENT_STATUS_OPTIONS } from 'Common/Constants';
import CustomInput from 'Common/CustomInput/CustomInput';
import CustomSelect from 'Common/CustomSelect';
import CustomTextarea from 'Common/CustomTextArea';
import Show from 'Components/Show/Show';
import PatientFilterSuperSearch from 'Components/PatientFilterSuperSearch/PatientFilterSuperSearch';
const PaymentPlanForm = ({ planType, formData, onChange, onAddNewCard, isEditing, stripeAccounts, creditCards,
  stripeloader, creditCardLoader, validationModel, showPatientSearch = false, onPatientSelect, superSearchValue, onSuperSearchValueChange }) => {

  const today = moment().format('YYYY-MM-DD');

  const getAuthorizationSubtitle = (planType) => {
    switch (planType) {
      case 'BAL':
        return 'Patient consent and authorization for balance payment plan';
      case 'SUB':
        return 'Patient consent and authorization for recurring subscription payments';
      case 'pretreatment':
        return 'Patient consent and authorization for pre-treatment payment plan';
      default:
        return 'Patient consent and authorization required';
    }
  };
  const getAuthorizationText = (planType) => {
    switch (planType) {
      case 'BAL':
        return 'I authorize the practice to charge my payment method according to the agreed schedule to pay off my outstanding balance. I understand the payment terms and my obligations under this payment plan.';
      case 'SUB':
        return 'I authorize the practice to charge my payment method on a recurring basis for the subscription services outlined in this plan. I understand I can cancel this subscription with proper notice as outlined in the practice policies.';
      case 'pretreatment':
        return 'I authorize the practice to charge my payment method according to the agreed schedule for the upcoming treatment. I understand that payments will be collected as scheduled regardless of any changes to the treatment date.';
      default:
        return 'I authorize the practice to charge my payment method according to the agreed payment schedule.';
    }
  };


  const calculateInstallmentAmount = (totalCost, downPayment, noOfInstallments) => {
    if (!totalCost || !noOfInstallments || noOfInstallments === 0) return 0;
    const remainingAmount = totalCost - (downPayment || 0);
    return remainingAmount / noOfInstallments;
  };

  const calculateEndDate = (firstPaymentDate, frequency, noOfInstallments) => {
    if (!firstPaymentDate || !noOfInstallments) return '';

    const finalPaymentDate = moment(firstPaymentDate, 'YYYY-MM-DD');

    for (let i = 0; i < noOfInstallments - 1; i++) {
      switch (frequency) {
        case 'D':
          finalPaymentDate.add(1, 'days');
          break;
        case 'W':
          finalPaymentDate.add(1, 'weeks');
          break;
        case 'BW':
          finalPaymentDate.add(2, 'weeks');
          break;
        case 'M':
          finalPaymentDate.add(1, 'months');
          break;
        case 'Q':
          finalPaymentDate.add(3, 'months');
          break;
        default:
          break;
      }
    }

    return finalPaymentDate.format('YYYY-MM-DD');
  };
  function calculateInstallments(firstPaymentDate, finalPaymentDate, selectedFrequency) {
    const start = moment(firstPaymentDate, "YYYY-MM-DD");
    const end = moment(finalPaymentDate, "YYYY-MM-DD");

    if (start.isAfter(end)) return 0;

    let unit, step;

    switch (selectedFrequency) {
      case "D":
        unit = "days"; step = 1; break;
      case "W":
        unit = "weeks"; step = 1; break;
      case "BW":
        unit = "weeks"; step = 2; break;
      case "M":
        unit = "months"; step = 1; break;
      case "Q":
        unit = "months"; step = 3; break;
      default:
        throw new Error("Invalid frequency selected");
    }

    const diff = end.diff(start, unit) / step;

    // +1 for the first installment
    return Math.floor(diff) + 1;
  }

  // Auto-calculate installment recurringAmount for pre-treatment plans
  useEffect(() => {

    if (planType === 'pretreatment' && formData.totalTreatmentCost && formData.noOfInstallments) {
      const calculatedAmount = calculateInstallmentAmount(
        formData.totalTreatmentCost,
        formData.downPayment || 0,
        formData.noOfInstallments
      );
      if (calculatedAmount !== formData.recurringAmount) {
        onChange('recurringAmount', Number(calculatedAmount)?.toFixed(2));
      }
    } else if (planType === 'BAL' && formData.totalBalance && formData.noOfInstallments) {
      const calculatedAmount = formData.totalBalance / formData.noOfInstallments;
      const roundedAmount = Number(calculatedAmount);
      if (Math.abs(roundedAmount - (formData.recurringAmount || 0)) > 0.01) {
        onChange('recurringAmount', Number(roundedAmount)?.toFixed(2));
      }
    }
  }, [formData.totalTreatmentCost, formData.downPayment, formData.noOfInstallments, planType, formData.totalBalance, formData.recurringAmount, onChange]);

  // Auto-calculate end date for pre-treatment and BAL plans
  useEffect(() => {
    if ((planType === 'pretreatment' || planType === 'BAL') && formData.firstPaymentDate && formData.noOfInstallments && formData.frequency) {
      const calculatedEndDate = calculateEndDate(
        formData.firstPaymentDate,
        formData.frequency,
        formData.noOfInstallments
      );
      if (calculatedEndDate !== formData.finalPaymentDate) {
        onChange('finalPaymentDate', calculatedEndDate);
      }
    }
  }, [formData.firstPaymentDate, formData.noOfInstallments, formData.frequency, planType, formData.finalPaymentDate, onChange]);
  // Auto-calculate noofinstallments
  useEffect(() => {
    if ((planType === 'SUB') && formData.firstPaymentDate && formData.frequency && formData.finalPaymentDate) {
      const calculatedNoOfInstallments = calculateInstallments(
        formData.firstPaymentDate,
        formData.finalPaymentDate,
        formData.frequency,
      );
      if (calculatedNoOfInstallments !== formData.noOfInstallments) {
        onChange('noOfInstallments', calculatedNoOfInstallments);
      }

    }
  }, [formData.firstPaymentDate, formData.noOfInstallments, formData.frequency, planType, formData.finalPaymentDate, onChange]);
  return (
    <div className="space-y-6">
      {/* Payment Plan Status (Edit mode only) */}
      {isEditing && (
        <FormSection
          title="Payment Plan Status"
          subtitle="Change the current status of this payment plan"
          icon={<LuActivity className="w-5 h-5" />}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {PAYMENT_STATUS_OPTIONS.map((status) => (
                <div
                  key={status.value}
                  className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.status === status.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => onChange('status', status.value)}
                >
                  <div className="flex items-start space-x-3">
                    <input type="radio" name="status" checked={formData.status === status.value} onChange={() => onChange('status', status.value)} className="w-4 h-4 mt-1 text-blue-600" />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{status.label}</h4>
                      <p className="mt-1 text-sm text-gray-500">
                        {status.planDescription}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Status Change Warning */}
            {formData.status === 'cancelled' && (
              <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                <div className="flex items-start">
                  <LuCircleAlert className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800">Cancellation Warning</h4>
                    <p className="mt-1 text-sm text-red-700">
                      Cancelling this payment plan will stop all future payments and cannot be undone.
                      Consider pausing the plan instead if this is temporary.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {formData.status === 'completed' && (
              <div className="p-4 border border-green-200 rounded-lg bg-green-50">
                <div className="flex items-start">
                  <LuCircleAlert className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-green-800">Completion Notice</h4>
                    <p className="mt-1 text-sm text-green-700">
                      Marking this plan as completed will indicate that all payments have been successfully processed.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {formData.status === 'paused' && (
              <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
                <div className="flex items-start">
                  <LuCircleAlert className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Pause Notice</h4>
                    <p className="mt-1 text-sm text-yellow-700">
                      Pausing this plan will temporarily stop automatic payments. You can reactivate it at any time.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </FormSection>
      )}
      {/* Treatment Information (Pre-treatment only) */}
      {planType === 'pretreatment' && (
        <FormSection
          title="Treatment Information"
          subtitle="Details about the upcoming procedure or treatment"
          icon={<LuStethoscope className="w-5 h-5" />}
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Procedure Description */}
            <div className="md:col-span-2">
              <label htmlFor="procedureDescription" className="block mb-2 text-sm font-medium text-gray-700">
                Procedure Description *
              </label>
              <input
                type="text"
                id="procedureDescription"
                value={formData.procedureDescription || ''}
                onChange={(e) => onChange('procedureDescription', e.target.value)}
                className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300  `}
                placeholder="e.g., Cosmetic dental procedure, Elective surgery"
              />

            </div>

            {/* Total Treatment Cost */}
            <div>
              <label htmlFor="totalTreatmentCost" className="block mb-2 text-sm font-medium text-gray-700">
                Total Treatment Cost *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  id="totalTreatmentCost"
                  step="0.01"
                  min="0"
                  value={formData.totalTreatmentCost || ''}
                  onChange={(e) => onChange('totalTreatmentCost', parseFloat(e.target.value) || 0)}
                  className={`block w-full pl-7 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300   }`}
                  placeholder="0.00"
                />
              </div>

            </div>

            {/* Down Payment */}
            <div>
              <label htmlFor="downPayment" className="block mb-2 text-sm font-medium text-gray-700">
                Down Payment (Optional)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  id="downPayment"
                  step="0.01"
                  min="0"
                  max={formData.totalTreatmentCost || undefined}
                  value={formData.downPayment || ''}
                  onChange={(e) => onChange('downPayment', parseFloat(e.target.value) || 0)}
                  className="block w-full py-2 pr-3 border border-gray-300 rounded-lg pl-7 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Amount to be paid before treatment begins
              </p>
            </div>

            {/* Treatment Date */}
            <div>
              <label htmlFor="treatmentDate" className="block mb-2 text-sm font-medium text-gray-700">
                Scheduled Treatment Date *
              </label>
              <input
                type="date"
                id="treatmentDate"
                value={formData.treatmentDate || ''}
                min={today}
                onChange={(e) => onChange('treatmentDate', e.target.value)}
                className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300`}
              />

            </div>

            {/* Remaining Balance Display */}
            {formData.totalTreatmentCost && (
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                <div className="mb-1 text-sm font-medium text-blue-900">Payment Summary</div>
                <div className="space-y-1 text-sm text-blue-800">
                  <div className="flex justify-between">
                    <span>Total Cost:</span>
                    <span>${formData.totalTreatmentCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Down Payment:</span>
                    <span>-${(formData.downPayment || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-1 font-medium border-t border-blue-300">
                    <span>Remaining Balance:</span>
                    <span>${(formData.totalTreatmentCost - (formData.downPayment || 0)).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </FormSection>
      )}

      {showPatientSearch && (
        <FormSection
          title="Patient"
          subtitle="Select patient for this payment plan"
          icon={<LuCircleUser className="w-5 h-5" />}
        >
          <div className='grid grid-cols-12 gap-6'>
            <div className='col-span-6'>
              <label className="block mb-2 text-sm font-medium text-gray-700 required">
                Patient
              </label>
              <PatientFilterSuperSearch
                handleSelectedData={(patient) => {
                  if (patient) {
                    onPatientSelect?.(patient);
                    onChange('patientId', patient.id);
                  } else {
                    onPatientSelect?.(null);
                    onChange('patientId', null);
                  }
                }}
                superSearchPatientValue={superSearchValue}
                setSuperSearchPatientValue={onSuperSearchValueChange}
                showFilterControls={false}
              />
              {validationModel.patientIdError}
            </div>
          </div>

        </FormSection>
      )}

      {/* Basic Information (Balance and Subscription only) */}
      {planType !== 'pretreatment' && (
        <FormSection
          title="Basic Information"
          icon={<LuDollarSign className="w-5 h-5" />}
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Balance Due (Balance-based only) */}
            {planType === 'BAL' && (
              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 z-30 flex items-center pl-3 pointer-events-none top-8">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <CustomInput
                    type="number"
                    id="totalBalance"
                    step="0.01"
                    min="0"
                    value={formData.totalBalance || ''}
                    onChange={(e) => onChange('totalBalance', parseFloat(e.target.value) || 0)}
                    className={`  pl-7 pr-3 `}
                    placeholder="0.00"
                    disabled
                    label='Balance Due'
                    required
                  />
                </div>
                {validationModel.totalBalanceError}
              </div>
            )}

            {/* Description */}
            <div className={planType === 'BAL' ? '' : 'w-[100%]'}>
              <CustomInput
                label="Plan Description"
                required
                id="planDescription"
                value={formData.planDescription || ''}
                onChange={(e) => onChange('planDescription', e.target.value)}
                placeholder={planType === 'BAL' ? 'e.g., Surgery payment plan' : 'e.g., Monthly wellness membership'}
              />
              {validationModel.planDescriptionError}
              <div className='text-right'>
                <span> {formData.planDescription?.length} </span>
                <span>/ 40</span>
              </div>
            </div>
          </div>
        </FormSection>
      )}

      {/* Payment Schedule */}
      <FormSection
        title={planType === 'pretreatment' ? 'Payment Schedule' : 'Payment Schedule'}
        subtitle={planType === 'pretreatment' ? 'Configure installment payments leading up to treatment' :
          planType === 'BAL' ? 'Configure installment payments for balance' : undefined}
        icon={<LuCalendar className="w-5 h-5" />}
      >
        <div className={`grid grid-cols-1 gap-6 ${planType === 'pretreatment' ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-4'
          }`}>
          {/* Installment Count (Pre-treatment and Balance plans) */}
          <Show when={(planType === 'pretreatment' || planType === 'BAL')}>
            <div>
              <CustomInput
                label={"Number of Installments"}
                required
                type="number"
                id="noOfInstallments"
                min="2"
                max="24"
                value={formData.noOfInstallments}
                onChange={(e) => onChange('noOfInstallments', e.target.value === "" ? 0 : parseInt(e.target.value, 10))}
                placeholder="2"
              />
              <p className="mt-1 text-xs text-gray-500">
                Payments will be split into this many installments
              </p>
              {validationModel.noOfInstallmentsError}
            </div>
          </Show>


          {/* Start Date */}
          <div>
            <CustomInput
              label="First Payment Date"
              required
              type="date"
              id="firstPaymentDate"
              value={formData.firstPaymentDate || ''}
              min={today}
              onChange={(e) => onChange('firstPaymentDate', e.target.value)}
            />
            {validationModel.firstPaymentDateError}
          </div>

          {/* End Date (not editable for pre-treatment and BAL, optional for subscription) */}
          <div>
            <CustomInput
              label={planType === 'pretreatment' || planType === 'BAL' ? 'Final Payment Date' : 'End Date (Optional)'}
              type="date"
              id="finalPaymentDate"
              value={formData.finalPaymentDate || ''}
              min={formData.firstPaymentDate || today}
              disabled={planType === 'pretreatment' || planType === 'BAL'}
              onChange={(e) => onChange('finalPaymentDate', e.target.value)}
            />
            {(planType === 'pretreatment' || planType === 'BAL') && (
              <p className="mt-1 text-xs text-gray-500">
                Automatically calculated based on installment count and frequency
              </p>
            )}
            {validationModel.finalPaymentDateError}

            <Show when={planType === 'SUB'}>
              {validationModel.noOfInstallmentsError}
            </Show>
          </div>

          {/* Frequency */}
          <div>
            <CustomSelect label="Frequency" required id="frequency" value={formData.frequency || 'monthly'} onChange={(e) => onChange('frequency', e.target.value)} >
              {/* dsfdsf (planType === 'SUB' then filter oute weekly and daily freqyuencies */}
              {PAYMENT_FREQUENCIES.filter(option => planType === 'SUB' ? option.value !== 'D' && option.value !== 'W' && option.value !== 'BW' : true
              ).map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </CustomSelect>
          </div>

          {/* Payment Amount (auto-calculated for pre-treatment) */}
          <div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 z-30 flex items-center pl-3 pointer-events-none top-8">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <CustomInput
                label='Payment Amount'
                required
                id="recurringAmount"
                min="0"
                isNumeric
                isFloat
                maxFloat={1}
                value={formData.recurringAmount}
                disabled={planType === 'pretreatment' || planType === 'BAL'}
                onChange={(e) => onChange('recurringAmount', e.target.value)}
                className={`pl-7 pr-3`}
                placeholder="0.00"
              />
            </div>
            {validationModel.recurringAmountError}
            {planType === 'pretreatment' && (
              <p className="mt-1 text-xs text-gray-500">
                Automatically calculated: (Total Cost - Down Payment) ÷ Installments
              </p>
            )}
            {planType === 'BAL' && (
              <p className="mt-1 text-xs text-gray-500">
                Automatically calculated: Balance Due ÷ Installments
              </p>
            )}

          </div>
        </div>
      </FormSection>

      {/* Patient Authorization (All plan types) */}
      <FormSection
        title="Patient Authorization"
        subtitle={
          <div className="flex items-center space-x-2">
            <span>{getAuthorizationSubtitle(planType)}</span>
            <a
              href="/pdfs/CC_Auth_Consent_Form.pdf"
              download="Payment_Plan_Authorization_Template.pdf"
              className="text-[#604FF8] hover:text-[#604FF8] underline text-sm font-medium"
            >
              Download Template
            </a>
          </div>
        }
        icon={<LuShield className="w-5 h-5" />}
      >
        <div className="space-y-6">
          {/* Pre-treatment specific warning */}
          {planType === 'pretreatment' && formData.finalPaymentDate && formData.treatmentDate &&
            new Date(formData.finalPaymentDate) > new Date(formData.treatmentDate) && (
              <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                <div className="flex items-start">
                  <LuCircleAlert className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-amber-800">Payment Schedule Notice</h4>
                    <p className="mt-1 text-sm text-amber-700">
                      The final payment is scheduled after the treatment date. Please ensure this aligns with your practice's payment policy.
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* Patient Authorization Confirmation */}
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="isPatientAuthorizationConfirmed"
              checked={formData.isPatientAuthorizationConfirmed || false}
              onChange={(e) => onChange('isPatientAuthorizationConfirmed', e.target.checked)}
              className="w-4 h-4 mt-1 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div className="flex-1">
              <label htmlFor="isPatientAuthorizationConfirmed" className="text-sm font-medium text-gray-700 required">
                Patient Authorization Confirmed
              </label>
              <p className="mt-1 text-sm text-gray-500">
                {getAuthorizationText(planType)}
              </p>
            </div>

          </div>
          {validationModel.isPatientAuthorizationConfirmedError}

        </div>
      </FormSection>

      {/* Payment Settings */}
      <FormSection
        title="Payment Settings"
        icon={<LuSettings className="w-5 h-5" />}
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12">

          {/* Stripe Account */}
          <div className="col-span-3">
            <CustomSelect
              label='  Stripe Account'
              required
              id="stripeSettingId"
              value={formData.stripeSettingId || 'main'}
              onChange={(e) => onChange('stripeSettingId', e.target.value)}
              disabled={stripeloader || creditCardLoader}
            >
              <option value="">Please Select</option>
              {stripeAccounts?.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.stripeAccount}
                </option>
              ))}
            </CustomSelect>

            <p className="mt-1 text-xs text-gray-500">
              Select which Stripe account to process payments
            </p>
            {validationModel.stripeSettingIdError}
          </div>
          {/* Grace Period ----- hide if planType is SUB */}
          <Show when={planType !== 'SUB'} fallback={<div className='col-span-9' />}>
            <div className="col-span-3">
              <CustomSelect
                label='Grace Period'
                id="gracePeriodInDays"
                value={formData.gracePeriodInDays}
                onChange={(e) => onChange('gracePeriodInDays', parseInt(e.target.value))}
                disabled={formData.autoCharge}
              >
                {GRACE_PERIOD_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </CustomSelect>
            </div>
          </Show>
          <Show when={planType === 'BAL'}>
            <div className='col-span-6' />

          </Show>

          {/* Credit Card Selection */}
          <div className="col-span-3">
            <label htmlFor="selectedCard" className="block mb-2 text-sm font-medium text-gray-700 required">
              Credit Card
            </label>
            {creditCards.length > 0 ? (
              <div className="space-y-3">
                {/* Saved Cards */}
                <div className="space-y-2">
                  {creditCards.map((card) => (
                    <div
                      key={card.id}
                      className={`relative p-3 border-2 rounded-lg cursor-pointer transition-all ${formData.paymentMethodId === card.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                      onClick={() => {
                        onChange('paymentMethodId', card.id);
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="selectedCard"
                          checked={formData.paymentMethodId === card.id}
                          onChange={() => {
                            onChange('paymentMethodId', card.id);
                          }}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div className="flex items-center space-x-2">
                          <LuCreditCard className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-900">
                            {card.cardType.toUpperCase()} •••• {card.cardNumber.slice(-4)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          Expires {card.expireDate}
                        </div>
                        {/* {card.isDefault && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Default
                          </span>
                        )} */}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add New Card Option */}
                <div
                  className={`relative p-3 border-2 rounded-lg cursor-pointer transition-all ${formData.paymentMethodId === 'new'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => {
                    onChange('paymentMethodId', 'new');
                    onChange('paymentMethod', 'card');
                    if (onAddNewCard) onAddNewCard();
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="selectedCard"
                      checked={formData.paymentMethodId === 'new'}
                      onChange={() => {
                        onChange('paymentMethodId', 'new');
                        onChange('paymentMethod', 'card');
                        if (onAddNewCard) onAddNewCard();
                      }}
                      className="h-4 w-4 text-[#604FF8]"
                    />
                    <div className="flex items-center space-x-2">
                      <LuPlus className="h-4 w-4 text-[#604FF8]" />
                      <span className="text-sm font-medium text-[#604FF8]">
                        Add New Card
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* No Saved Cards - Show Add New Card */
              <button
                type="button"
                onClick={() => {
                  onChange('paymentMethodId', 'new');
                  onChange('paymentMethod', 'card');
                  if (onAddNewCard) onAddNewCard();
                }}
                className="flex items-center justify-center w-full px-4 py-3 transition-colors border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <LuPlus className="w-5 h-5 mr-2 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Add Credit Card</span>
              </button>
            )}
          </div>

          <div className="col-span-3">
            <AsyncSelect
              value={formData.locationId}
              name="locationId"
              onChange={(e) => onChange('locationId', parseInt(e.target.value))}
              label="Locations"
              required
              apiLink={`${process.env.REACT_APP_TC_PMS}/api/DropDown/FindLocations`}
            />
            {validationModel.locationIdError}

          </div>

          {/* Auto-charge Toggle     ----  disable if planType is SUB */}
          <div className="col-span-4">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Auto-charge
            </label>
            <div className="flex items-center">
              <button
                type="button"
                disabled={planType === 'SUB'}
                onClick={() => onChange('autoCharge', !formData.autoCharge)}
                className={`relative disabled:cursor-not-allowed disabled:opacity-50 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${formData.autoCharge ? 'bg-[#604FF8]' : 'bg-gray-200'
                  }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.autoCharge ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
              </button>
              <span className="ml-3 text-sm text-gray-700">
                {formData.autoCharge ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Automatically charge the payment method on due dates
            </p>
          </div>
        </div>
      </FormSection>

      {/* Internal Notes */}
      <FormSection
        title="Internal Notes"
        subtitle={planType === 'pretreatment' ?
          "Notes about the treatment plan and payment arrangement" :
          "These notes are for internal use only and won't be visible to the patient"}
      >
        <div>
          <CustomTextarea
            id="internalNotes"
            maxWidth="100%"
            rows={3}
            value={formData.internalNotes || ''}
            onChange={(e) => onChange('internalNotes', e.target.value)}
            placeholder={planType === 'pretreatment' ?
              "Add notes about the procedure, patient preferences, or special considerations..." :
              "Add any internal notes about this payment plan..."}
          />
          <div className='text-right'>
            <span> {formData.internalNotes?.length} </span>
            <span>/ 500</span>
          </div>
        </div>
      </FormSection>
    </div>
  )
}

export default PaymentPlanForm